import infoIcon from "@loot-os/icons/icons/info";
import warningIcon from "@loot-os/icons/icons/warning";
import { Badge } from "../core/Badge.js";
import { Popover } from "../core/Popover.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";

export interface NoticeBadgeProps {
  /** The badge's words: "3 left", "Limited item", "Can't ship". */
  label: string;
  /** The popover's title, when it differs from the badge's words ("Limited item" on "3 left"). */
  title?: string;
  /** What it means for the shopper, one rule per line, in the store's words. Shown whole. */
  lines: readonly string[];
  /** `danger` for what the shopper can no longer do (ship it); `warning` for a rule to know. */
  tone?: "warning" | "danger";
  /** The button's accessible name; defaults to its words, the title, and "what this means". */
  triggerLabel?: string;
  className?: string;
}

/**
 * A short status that opens the rules behind it: an item whose supply is counted, one that can
 * no longer be shipped. It sits on an item's art so the card keeps its height, and a shopper
 * reads the whole reason before spending anything. A compliance surface: the lines are shown
 * whole, never truncated.
 */
export function NoticeBadge({
  label,
  title = label,
  lines,
  tone = "warning",
  triggerLabel,
  className,
}: NoticeBadgeProps) {
  return (
    <Popover
      title={title}
      trigger={
        <button
          type="button"
          // The badge's own words first, so a voice user can say what they see (WCAG 2.5.3).
          aria-label={
            triggerLabel ??
            (title === label ? `${label}: what this means` : `${label}, ${title}: what this means`)
          }
          className={cn("flex w-fit max-w-full cursor-pointer rounded-pill", FOCUS_RING, className)}
        >
          <Badge tone={tone} icon={tone === "danger" ? warningIcon : infoIcon}>
            {label}
          </Badge>
        </button>
      }
    >
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </Popover>
  );
}
